/**
 * StockResearchHero - Research-first center panel for the Stock Analysis page.
 *
 * UI States:
 * - idle: Centered hero input with ticker search + analysis config + suggestion chips
 * - researching: Inline FinancialLogicTree streaming
 * - complete: Inline FinancialAnalysisPanel with kill sheet
 *
 * Market intelligence data is shown as supplemental content below the hero in idle state.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Zap, ArrowLeft, RotateCcw, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialStream } from '@/hooks/useFinancialStream';
import { useCredits } from '@/hooks/useCredits';
import { useTickerSearch, type TickerSearchResult } from '@/hooks/useTickerSearch';
import { FinancialLogicTree, type FinancialResearchDepth } from '@/components/verdict/FinancialLogicTree';
import { FinancialAnalysisPanel } from '@/components/verdict/FinancialAnalysisPanel';
import { MarketSummaryBar } from './MarketSummaryBar';
import { TrendingTickersCard, type MarketMover } from './TrendingTickersCard';
import { MarketNewsSection, type MarketNewsItem } from './MarketNewsSection';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type { PortfolioSummary, PositionWithScan, Position } from '@/types/portfolio';

type AnalysisMode = 'general' | 'earnings_play' | 'long_term_hold' | 'valuation_focus';

interface MarketPulseData {
  market_news: MarketNewsItem[];
  top_gainers: MarketMover[];
  top_losers: MarketMover[];
  most_active: MarketMover[];
}

interface StockResearchHeroProps {
  onTickerSelect: (ticker: string) => void;
  onDeepAnalysis: (ticker: string) => void;
  marketPulse: MarketPulseData | null;
  isLoadingPulse: boolean;
  summary: PortfolioSummary | null;
  positionCount: number;
  positionsWithScan: PositionWithScan[];
  portfolioPositions: Position[];
  initialQuery?: string;
  initialTicker?: string;
  /** When provided, delegate analysis to parent instead of managing own stream.
   *  Hero stays in idle mode and calls this callback. */
  onAnalysisStart?: (query: string, ticker: string | null, depth: FinancialResearchDepth) => void;
}

type UIState = 'idle' | 'researching' | 'complete';

const DEPTH_OPTIONS: { value: FinancialResearchDepth; label: string; agents: string }[] = [
  { value: 'fast', label: 'Fast', agents: '4 agents' },
  { value: 'deep', label: 'Deep', agents: '6 agents' },
];

const SUGGESTION_CHIPS = [
  { label: 'AAPL', query: 'Analyze AAPL stock' },
  { label: 'NVDA', query: 'Analyze NVDA stock' },
  { label: 'TSLA Earnings', query: 'Analyze TSLA as an earnings play opportunity' },
  { label: 'MSFT vs GOOG', query: 'Compare MSFT vs GOOG' },
];

function buildQuestion(ticker: string, mode: AnalysisMode): string {
  switch (mode) {
    case 'earnings_play':
      return `Analyze ${ticker} as an earnings play opportunity. Focus on upcoming earnings, expected EPS, revenue estimates, options implied volatility, and short-term catalysts.`;
    case 'long_term_hold':
      return `Analyze ${ticker} as a long-term hold. Focus on fundamentals, competitive moat, management quality, revenue growth trajectory, and multi-year thesis.`;
    case 'valuation_focus':
      return `Analyze ${ticker} with focus on valuation. Evaluate DCF, comparable company analysis, margin of safety, P/E relative to growth, and whether current price is justified.`;
    case 'general':
    default:
      return `Analyze ${ticker} stock`;
  }
}

export function StockResearchHero({
  onTickerSelect,
  marketPulse,
  isLoadingPulse,
  summary,
  positionCount,
  positionsWithScan: _positionsWithScan,
  portfolioPositions,
  initialQuery,
  initialTicker,
  onAnalysisStart,
}: StockResearchHeroProps) {
  const [uiState, setUiState] = useState<UIState>('idle');
  const [query, setQuery] = useState('');
  const [depth, setDepth] = useState<FinancialResearchDepth>('fast');
  const [analysisMode] = useState<AnalysisMode>('general');
  const [activeQuery, setActiveQuery] = useState('');
  const consumedInitial = useRef(false);

  const { balance, isUnlimited } = useCredits();
  const { results: tickerResults, isLoading: isSearching, search: searchTickers, clear: clearTickerSearch } = useTickerSearch();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const {
    state: financialState,
    isConnected,
    isConnecting,
    error,
    startAnalysis,
    reset: resetAnalysis,
  } = useFinancialStream({
    onComplete: () => {
      setUiState('complete');
    },
    onError: (err) => {
      console.error('StockResearchHero analysis error:', err);
    },
  });

  const isRunning = isConnected || isConnecting || (financialState.phase !== 'idle' && financialState.phase !== 'concluded' && financialState.phase !== 'error');

  // Handle initial query from URL param (?q=)
  useEffect(() => {
    if (initialQuery && !consumedInitial.current && financialState.phase === 'idle') {
      consumedInitial.current = true;
      if (onAnalysisStart) {
        onAnalysisStart(initialQuery, null, depth);
      } else {
        setQuery(initialQuery);
        setActiveQuery(initialQuery);
        setUiState('researching');
        startAnalysis(null, depth, initialQuery);
      }
    }
  }, [initialQuery, financialState.phase, depth, startAnalysis, onAnalysisStart]);

  // Handle initial ticker from URL param (?ticker=)
  useEffect(() => {
    if (initialTicker && !initialQuery && !consumedInitial.current && financialState.phase === 'idle') {
      consumedInitial.current = true;
      const tickerUpper = initialTicker.toUpperCase();
      const question = buildQuestion(tickerUpper, analysisMode);
      if (onAnalysisStart) {
        onAnalysisStart(question, tickerUpper, depth);
      } else {
        setQuery(tickerUpper);
        setActiveQuery(question);
        setUiState('researching');
        onTickerSelect(tickerUpper);
        startAnalysis(tickerUpper, depth, question, {
          portfolioContext: portfolioPositions.some(p => p.symbol === tickerUpper),
        });
      }
    }
  }, [initialTicker, initialQuery, financialState.phase, depth, analysisMode, startAnalysis, portfolioPositions, onTickerSelect, onAnalysisStart]);

  const handleSubmit = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;

    // Determine if input is a ticker or a question
    const isSingleTicker = /^[A-Z]{1,5}$/i.test(trimmed) && !trimmed.includes(' ');
    const tickerUpper = trimmed.toUpperCase();

    let question: string;
    let ticker: string | null = null;

    if (isSingleTicker) {
      ticker = tickerUpper;
      question = buildQuestion(tickerUpper, analysisMode);
    } else {
      question = trimmed;
    }

    if (onAnalysisStart) {
      onAnalysisStart(question, ticker, depth);
      return;
    }

    setActiveQuery(question);
    setUiState('researching');

    const isHeld = ticker ? portfolioPositions.some(p => p.symbol === ticker) : false;
    startAnalysis(ticker, depth, question, { portfolioContext: isHeld });
  }, [query, analysisMode, depth, portfolioPositions, startAnalysis, onAnalysisStart]);

  const handleSuggestionClick = useCallback((suggestion: typeof SUGGESTION_CHIPS[0]) => {
    if (onAnalysisStart) {
      onAnalysisStart(suggestion.query, null, depth);
      return;
    }
    setQuery(suggestion.label);
    setActiveQuery(suggestion.query);
    setUiState('researching');
    startAnalysis(null, depth, suggestion.query);
  }, [depth, startAnalysis, onAnalysisStart]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (val.trim().length > 0) {
      searchTickers(val);
      setShowSuggestions(true);
    } else {
      clearTickerSearch();
      setShowSuggestions(false);
    }
  }, [searchTickers, clearTickerSearch]);

  const handleTickerSuggestionSelect = useCallback((result: TickerSearchResult) => {
    setQuery(result.symbol);
    setShowSuggestions(false);
    clearTickerSearch();
  }, [clearTickerSearch]);

  const handleClearInput = useCallback(() => {
    setQuery('');
    clearTickerSearch();
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [clearTickerSearch]);

  const handleNewAnalysis = useCallback(() => {
    resetAnalysis();
    setQuery('');
    setActiveQuery('');
    setUiState('idle');
    consumedInitial.current = false;
  }, [resetAnalysis]);

  const handleBack = useCallback(() => {
    if (isRunning) return; // Don't allow back while running
    handleNewAnalysis();
  }, [isRunning, handleNewAnalysis]);

  const handleTickerClick = useCallback((ticker: string) => {
    onTickerSelect(ticker);
  }, [onTickerSelect]);

  // Idle state — hero input
  if (uiState === 'idle') {
    return (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center min-h-[35vh] pt-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <Search className="w-7 h-7 text-primary" />
          </div>

          <h2 className="text-2xl font-bold text-text-primary mb-1 text-center">
            Stock Research
          </h2>
          <p className="text-text-muted text-sm text-center max-w-md mb-6">
            AI-powered bull vs bear analysis with kill sheet verdict
          </p>

          {/* Input Area */}
          <div className="w-full max-w-lg">
            <div className="space-y-3">
              {/* Hybrid input: free-text with optional ticker suggestions */}
              <div ref={suggestionsRef} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted z-10" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      setShowSuggestions(false);
                      handleSubmit();
                    } else if (e.key === 'Escape') {
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (tickerResults.length > 0) setShowSuggestions(true);
                  }}
                  placeholder="Search ticker, company name, or ask a question..."
                  className={cn(
                    'w-full pl-10 pr-10 py-3 rounded-lg',
                    'bg-surface-light border border-border',
                    'text-text-primary placeholder:text-text-muted',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
                    'transition-colors duration-200',
                  )}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isSearching ? (
                    <Loader2 className="w-4 h-4 text-text-muted animate-spin" />
                  ) : query ? (
                    <button
                      type="button"
                      onClick={handleClearInput}
                      className="p-0.5 rounded text-text-muted hover:text-text-primary transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>

                {/* Ticker suggestions dropdown */}
                {showSuggestions && tickerResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                    {tickerResults.slice(0, 5).map((result) => (
                      <button
                        key={`${result.symbol}-${result.exchange}`}
                        type="button"
                        onClick={() => handleTickerSuggestionSelect(result)}
                        className="w-full px-4 py-2.5 text-left flex items-center justify-between hover:bg-surface-light transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <TickerLogo symbol={result.symbol} name={result.name} size="xs" />
                          <span className="font-mono font-semibold text-text-primary text-sm shrink-0">
                            {result.symbol}
                          </span>
                          <span className="text-xs text-text-muted truncate">
                            {result.name}
                          </span>
                        </div>
                        <span className="text-[10px] text-text-muted shrink-0 ml-2">
                          {result.exchange}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Depth selector */}
              <div className="flex items-center gap-2">
                {DEPTH_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDepth(option.value)}
                    className={cn(
                      'flex-1 px-3 py-2 rounded-lg border text-center transition-all duration-200',
                      depth === option.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-surface-light text-text-muted hover:border-text-muted',
                    )}
                  >
                    <div className="font-medium text-xs">{option.label}</div>
                    <div className="text-[10px] text-text-muted mt-0.5">{option.agents}</div>
                  </button>
                ))}
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!query.trim()}
                className={cn(
                  'w-full py-3 rounded-lg font-semibold text-sm',
                  'flex items-center justify-center gap-2',
                  'transition-all duration-300',
                  query.trim()
                    ? 'bg-primary text-text-primary hover:bg-primary/90 shadow-lg shadow-primary/25'
                    : 'bg-surface-light text-text-muted cursor-not-allowed',
                )}
              >
                <Zap className="w-4 h-4" />
                {query.trim()
                  ? `Run Analysis${/^[A-Z]{1,5}$/i.test(query.trim()) ? ` on ${query.trim().toUpperCase()}` : ''}`
                  : 'Enter a ticker or question'
                }
              </button>

              {/* Credit info */}
              {balance !== null && !isUnlimited && (
                <p className="text-[10px] text-text-muted text-center">
                  {balance} credits remaining
                </p>
              )}
            </div>
          </div>

          {/* Suggestion Chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            <span className="text-[10px] text-text-muted mr-1">Try:</span>
            {SUGGESTION_CHIPS.map((chip) => (
              <button
                key={chip.label}
                onClick={() => handleSuggestionClick(chip)}
                className="px-3 py-1.5 rounded-full border border-border bg-surface-light/50 text-xs text-text-secondary hover:border-primary/50 hover:text-primary transition-colors"
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Supplemental: Market Intelligence */}
        {(marketPulse || isLoadingPulse || summary) && (
          <div className="border-t border-border pt-6 space-y-4">
            <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider px-1">
              Market Overview
            </h3>
            <MarketSummaryBar summary={summary} positionCount={positionCount} />
            <TrendingTickersCard
              gainers={marketPulse?.top_gainers || []}
              losers={marketPulse?.top_losers || []}
              mostActive={marketPulse?.most_active || []}
              isLoading={isLoadingPulse}
              onTickerClick={handleTickerClick}
            />
            <MarketNewsSection
              news={marketPulse?.market_news || []}
              isLoading={isLoadingPulse}
              onTickerClick={handleTickerClick}
            />
          </div>
        )}
      </div>
    );
  }

  // Researching / Complete states
  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!isRunning && (
            <button
              onClick={handleBack}
              className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          {financialState.ticker && (() => {
            const tickers = financialState.ticker.split(' vs ');
            const isComparison = tickers.length > 1;

            if (isComparison) {
              return (
                <div className="flex items-center gap-3">
                  {/* Ticker A logo + symbol */}
                  <div className="flex items-center gap-2">
                    <TickerLogo symbol={tickers[0]} size="md" />
                    <span className="text-lg font-bold text-text-primary font-mono">{tickers[0]}</span>
                  </div>
                  {/* VS badge */}
                  <span className="px-2 py-0.5 rounded-md bg-primary/10 border border-primary/20 text-[10px] font-bold text-primary uppercase tracking-wider">
                    vs
                  </span>
                  {/* Ticker B logo + symbol */}
                  <div className="flex items-center gap-2">
                    <TickerLogo symbol={tickers[1]} size="md" />
                    <span className="text-lg font-bold text-text-primary font-mono">{tickers[1]}</span>
                  </div>
                  {/* Status */}
                  <span className="text-xs text-text-muted ml-1">
                    {uiState === 'researching' ? '— Analyzing...' : '— Complete'}
                  </span>
                </div>
              );
            }

            return (
              <div className="flex items-center gap-2">
                <TickerLogo symbol={tickers[0]} size="md" />
                <div>
                  <h2 className="text-lg font-bold text-text-primary font-mono">
                    {financialState.ticker}
                  </h2>
                  <p className="text-xs text-text-muted">
                    {uiState === 'researching' ? 'Analyzing...' : 'Analysis Complete'}
                  </p>
                </div>
              </div>
            );
          })()}
          {!financialState.ticker && (
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Running Analysis
              </h2>
              <p className="text-xs text-text-muted truncate max-w-[300px]">
                {activeQuery}
              </p>
            </div>
          )}
        </div>

        {uiState === 'complete' && (
          <button
            onClick={handleNewAnalysis}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            New Analysis
          </button>
        )}
      </div>

      {/* Error state */}
      {error && financialState.phase !== 'error' && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Streaming progress */}
      {uiState === 'researching' && financialState.phase !== 'idle' && (
        <FinancialLogicTree
          state={financialState}
          query={activeQuery}
          onNewResearch={handleNewAnalysis}
        />
      )}

      {/* Completed kill sheet */}
      {uiState === 'complete' && financialState.killSheet && (
        <FinancialAnalysisPanel
          killSheet={financialState.killSheet}
          anchoredSnapshot={financialState.hardDataSnapshot}
          convergenceTrajectory={financialState.convergenceTrajectory}
          initialDivergence={financialState.initialDivergence}
          echoChamberedDetected={financialState.echoChamberedDetected}
        />
      )}

      {/* Fallback error with action */}
      {financialState.phase === 'error' && financialState.fallbackData && (
        <div className="rounded-xl border border-border bg-surface p-6 text-center space-y-3">
          <p className="text-sm text-text-secondary">
            {financialState.fallbackData.reason}
          </p>
          <p className="text-xs text-text-muted">
            {financialState.fallbackData.suggestion}
          </p>
          <button
            onClick={handleNewAnalysis}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Try Again
          </button>
        </div>
      )}

      {/* Generic error without fallback */}
      {financialState.phase === 'error' && !financialState.fallbackData && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-6 text-center space-y-3">
          <p className="text-sm text-red-400">
            {financialState.error || error || 'An error occurred during analysis'}
          </p>
          <button
            onClick={handleNewAnalysis}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
